import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DotLottleLoader } from '../../../components/Loader';
import { useUser } from '../../../hooks/useUser';
import axiosInstant from '../../../api';
import { getBrandTeamEndpoint } from '../../../api/endpoints';
import { ReactComponent as AddIcon } from '../../../assets/svg/add-icon.svg';
import { ReactComponent as NoTeamMembersIcon } from '../../../assets/svg/no-team-members.svg';
import { usePortalPath } from '../../../hooks/usePortalPath'

const TeamMembers: React.FC = () => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const { user, role } = useUser();
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const isBrandAdmin = role === 'BRAND_ADMIN';

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);

      const brandId = user?.brand?.[0]?.id;
      if (!brandId) {
        setTeamMembers([]);
        return;
      }

      const response = await axiosInstant.get(getBrandTeamEndpoint(brandId.toString()));

      if (response.data) {
        let members: any = [];

        if (Array.isArray(response.data)) {
          members = response.data;
        } else if (response.data.rows) {
          members = response.data.rows;
        } else if (response.data.data) {
          members = response.data.data;
        }

        setTeamMembers(members || []);
      } else {
        setTeamMembers([]);
      }

    } catch (error) {
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchTeamMembers();
    }
  }, [user]);

  const handleAddTeamMember = () => {
    navigate(p('/settings/team?new=true'));
  };

  const handleViewAll = () => {
    navigate(p('/settings/team'));
  };

  const getInitials = (member: any) => {
    if (member.firstName && member.lastName) {
      return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
    }
    const name = member.displayName || member.emailAddress?.split('@')[0] || '?';
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full min-h-0">
        <DotLottleLoader fill={false} />
      </Box>
    );
  }

  if (teamMembers.length === 0) {
    return (
      <Box className="flex flex-col h-full min-h-0">
        <Box className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography
            sx={{
              color: 'var(--text)',
              fontFamily: 'CircularStd',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            Team
          </Typography>
        </Box>

        <Box className="flex-1 flex flex-col items-center justify-center w-full" sx={{ gap: '21px' }}>
          <NoTeamMembersIcon />

          <Typography
            sx={{
              color: 'var(--Base-600, #727272)',
              fontFamily: '"CircularStd"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 450,
              lineHeight: 'normal',
              textAlign: 'center',
            }}
          >
            You don't currently have any team members
          </Typography>

          <Button
            onClick={handleAddTeamMember}
            sx={{
              backgroundColor: 'var(--accent)',
              color: 'var(--Base-0, #FFF)',
              fontFamily: 'CircularStd',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 900,
              lineHeight: 'normal',
              textTransform: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              '&:hover': {
                backgroundColor: '#8A6D41',
              },
            }}
          >
            <AddIcon />
            Add Team Member
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="flex flex-col h-full min-h-0">
      <Box className="flex items-center justify-between mb-4 flex-shrink-0 max-md:flex-wrap max-md:gap-2">
        <Typography
          sx={{
            color: 'var(--text)',
            fontFamily: 'CircularStd',
            fontSize: { xs: '15px', md: '18px' },
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: 'normal',
          }}
        >
          Team ({teamMembers.length})
        </Typography>
        {isBrandAdmin && (
          <Box className="flex items-center gap-4 max-md:gap-2">
            <Button
              onClick={handleViewAll}
              sx={{
                color: 'var(--accent-text)',
                fontFamily: 'CircularStd',
                fontSize: { xs: '12px', md: '14px' },
                fontWeight: 450,
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              Team Settings
            </Button>
            <Button
              onClick={handleAddTeamMember}
              sx={{
                backgroundColor: 'var(--accent)',
                color: 'var(--Base-0, #FFF)',
                fontFamily: 'CircularStd',
                fontSize: { xs: '11px', md: '14px' },
                fontStyle: 'normal',
                fontWeight: 900,
                lineHeight: 'normal',
                textTransform: 'none',
                borderRadius: '12px',
                padding: { xs: '6px 12px', md: '8px 16px' },
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#8A6D41',
                },
              }}
            >
              + Add Team Member
            </Button>
          </Box>
        )}
      </Box>

      <Box
        className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
        sx={{
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': { backgroundColor: '#a8a8a8' },
          },
        }}
      >
        <Box
          className="flex gap-4 pb-2"
          style={{
            width: 'max-content',
            paddingRight: '16px'
          }}
        >
          {teamMembers.map((member) => (
            <Box
              key={member.userid}
              className="flex-shrink-0 bg-surface rounded-xl border border-border-token"
              sx={{
                width: { xs: '230px', md: '291px' },
                padding: '28px 16px 0px 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                borderRadius: '16px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--surface)',
                boxSizing: 'border-box',
                minHeight: '257px',
                maxHeight: '320px',
                overflow: 'hidden'
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  flexShrink: 0,
                }}
              >
                {member.avatar && member.avatar.trim() !== '' ? (
                  <img
                    src={member.avatar}
                    alt={member.displayName}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <Typography
                    sx={{
                      color: 'var(--muted)',
                      fontFamily: '"CircularStd"',
                      fontSize: '28px',
                      fontStyle: 'normal',
                      fontWeight: 900,
                      lineHeight: '132%',
                      textAlign: 'center',
                    }}
                  >
                    {getInitials(member)}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: 'var(--border)',
                }}
              />

              <Typography
                sx={{
                  color: 'var(--text)',
                  fontFamily: 'CircularStd',
                  fontSize: '22px',
                  fontStyle: 'normal',
                  fontWeight: 900,
                  lineHeight: '132%',
                  textAlign: 'center',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={member.displayName ||
                  (member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : null) ||
                  member.emailAddress?.split('@')[0] ||
                  'Unknown User'}
              >
                {member.displayName ||
                  (member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : null) ||
                  member.emailAddress?.split('@')[0] ||
                  'Unknown User'}
              </Typography>

              <Typography
                sx={{
                  // --muted is 3.6:1 on --surface in dark (spec: placeholders and
                  // decorative icons only). Real meta text uses --text-2.
                  color: 'var(--text-2)',
                  fontFamily: 'CircularStd',
                  fontSize: '15px',
                  fontStyle: 'normal',
                  fontWeight: 900,
                  lineHeight: '132%',
                  textAlign: 'center',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={member.emailAddress}
              >
                {member.emailAddress}
              </Typography>

              <Typography
                sx={{
                  color: 'var(--text-2)',
                  fontFamily: 'CircularStd',
                  fontSize: '15px',
                  fontStyle: 'normal',
                  fontWeight: 450,
                  lineHeight: '132%',
                  textAlign: 'center',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={member.role?.name || member.usertype?.toUpperCase()}
              >
                {member.role?.name || member.usertype?.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TeamMembers;
